import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import DriverFilter, { useDrivers } from "@/components/DriverFilter";
import { activity, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { Truck, CarProfile, ClipboardText, ArrowRight } from "@phosphor-icons/react";

const KINDS = [
  { id: "", label: "Everything" }, { id: "load", label: "Loads" },
  { id: "vehicle", label: "Vehicles" }, { id: "inspection", label: "Inspections" },
];
const ICONS = { load: Truck, vehicle: CarProfile, inspection: ClipboardText };

export default function Activity() {
  const { user } = useAuth();
  const isOwner = user?.fleet_role === "owner";
  const drivers = useDrivers(isOwner);
  const [driver, setDriver] = useState("");
  const [kind, setKind] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!isOwner) { return; }
    setItems(null);
    activity.feed({ driver: driver || undefined, kind: kind || undefined }).then(setItems)
      .catch((e) => { toast.error(formatErr(e)); setItems([]); });
  }, [driver, kind, isOwner]);
  if (!isOwner) return <Navigate to="/app" replace/>;

  return <AppShell>
    <div className="mb-6">
      <div className="eyebrow text-accent">Fleet owner</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Driver activity</h1>
      <p className="text-sm text-muted-foreground mt-1">Who added each load, vehicle and inspection in your fleet.</p>
    </div>
    <div className="flex flex-wrap items-center gap-4 mb-5 bg-white border border-border/60 p-3">
      <DriverFilter drivers={drivers} value={driver} onChange={setDriver} tid="activity-driver-filter"/>
      <div className="flex flex-wrap gap-1" data-testid="activity-kind-tabs">
        {KINDS.map(k => (
          <button key={k.id} data-testid={`activity-kind-${k.id || "all"}`} onClick={() => setKind(k.id)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${kind === k.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-accent"}`}>
            {k.label}
          </button>
        ))}
      </div>
      {items && <span className="ml-auto eyebrow text-muted-foreground" data-testid="activity-count">{items.length} records</span>}
    </div>
    <ActivityList items={items}/>
  </AppShell>;
}

function ActivityList({ items }) {
  if (!items) return <div className="text-muted-foreground" data-testid="activity-loading">Loading…</div>;
  if (!items.length) return <div className="border-2 border-dashed p-10 text-center text-muted-foreground" data-testid="activity-empty">No activity for this filter.</div>;
  return <ol className="space-y-2" data-testid="activity-list">
    {items.map(it => <ActivityRow key={`${it.kind}-${it.id}`} it={it}/>)}
  </ol>;
}

function ActivityRow({ it }) {
  const Icon = ICONS[it.kind];
  return <li>
    <Link to={it.link} data-testid={`activity-item-${it.id}`} className="flex items-start sm:items-center gap-3 sm:gap-4 bg-white border border-border/60 p-4 hover:border-accent transition-colors">
      <Icon size={26} weight="duotone" className="text-primary shrink-0"/>
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold truncate">{it.title}</div>
          <div className="text-sm text-muted-foreground truncate">{it.detail}</div>
        </div>
        <div className="sm:text-right shrink-0">
          <div className="text-sm font-bold" data-testid={`activity-added-by-${it.id}`}>{it.added_by}</div>
          <div className="font-mono-tech text-[11px] text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleString() : "—"}</div>
        </div>
      </div>
      <ArrowRight size={16} className="shrink-0 self-center"/>
    </Link>
  </li>;
}
